import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Filter, Download, TrendingUp, TrendingDown, Minus,
  Truck, Package, ShoppingCart, DollarSign, Phone, Mail, MapPin, Edit,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0) + ' FCFA';
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.0', '') + ' M';
  if (v >= 1_000) return (v / 1_000).toFixed(0) + ' k';
  return String(v);
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};
const initials = (s = '') => (s.trim().split(/\s+/)[0] || '•').charAt(0).toUpperCase();

const CAT_COLORS = {
  'Services & Prestations': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'Matériel de bureau':     { bg: 'bg-violet-100',  text: 'text-violet-700' },
  'Textile':                { bg: 'bg-amber-100',   text: 'text-amber-700' },
  'Imprimerie':             { bg: 'bg-sky-100',     text: 'text-sky-700' },
  'Autre':                  { bg: 'bg-blue-100',    text: 'text-blue-700' },
};
const avatarPalette = ['bg-emerald-500', 'bg-violet-600', 'bg-orange-500', 'bg-blue-500', 'bg-rose-500', 'bg-sky-600', 'bg-amber-500', 'bg-rose-700'];
const avatarColor = (code, idx) => avatarPalette[((code?.charCodeAt(0) || 0) + idx) % avatarPalette.length];

export default function Fournisseurs({ user, onLogout }) {
  const [list, setList] = useState([]);
  const [kpis, setKpis] = useState({ actifs: 0, commandes_en_cours: 0, achats_cumules: 0, livraison_temps_pct: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/fournisseurs/ranking/list`);
      setList(r.data.fournisseurs || []);
      setKpis(r.data.kpis || {});
      if ((r.data.fournisseurs || []).length > 0 && !selectedId) {
        setSelectedId(r.data.fournisseurs[0].id);
      }
    } catch (e) {
      toast.error('Erreur chargement fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!selectedId) return;
    axios.get(`${API}/api/fournisseurs/${selectedId}/performance`)
      .then(r => setDetails(r.data))
      .catch(() => toast.error('Erreur chargement fiche'));
  }, [selectedId]);

  return (
    <Layout user={user} onLogout={onLogout} currentPage="fournisseurs">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5" data-testid="fournisseurs-root">
        {/* ─────── Colonne principale ─────── */}
        <main className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900">Fournisseurs</h1>
              <p className="text-sm text-slate-500">Classement par performance, du plus fiable au plus à risque</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1" data-testid="btn-filtres"><Filter className="h-3.5 w-3.5" /> Filtres</Button>
              <Button variant="outline" size="sm" className="gap-1" data-testid="btn-exporter"><Download className="h-3.5 w-3.5" /> Exporter</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => setShowAdd(true)} data-testid="btn-nouveau-fournisseur">
                <Plus className="h-3.5 w-3.5" /> Nouveau fournisseur
              </Button>
            </div>
          </div>

          {/* 4 KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Fournisseurs actifs"  value={kpis.actifs}                sub="+2 ce trimestre"   subColor="text-emerald-600" />
            <KpiCard label="Commandes en cours"   value={kpis.commandes_en_cours}    sub="3 à réceptionner"  subColor="text-slate-500" />
            <KpiCard label="Achats cumulés"       value={`${fmtShort(kpis.achats_cumules)} FCFA`} sub="+12 % vs 2025" subColor="text-emerald-600" />
            <KpiCard label="Livraison à temps"    value={`${kpis.livraison_temps_pct} %`} sub="−3 pts ce mois" subColor="text-rose-600" />
          </div>

          {/* Tableau classement */}
          <Card className="rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50/50">
                      <th className="py-3 px-4">Fournisseur</th>
                      <th className="py-3 px-2">Catégorie</th>
                      <th className="py-3 px-2">Contact</th>
                      <th className="py-3 px-2 text-right">Commandes</th>
                      <th className="py-3 px-2 text-right">Achats (FCFA)</th>
                      <th className="py-3 px-2 w-[140px]">Livraison à temps</th>
                      <th className="py-3 px-2 w-[140px]">Qualité</th>
                      <th className="py-3 px-2 text-center">Score</th>
                      <th className="py-3 px-4">Tendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={9} className="py-10 text-center text-slate-400">Chargement…</td></tr>
                    )}
                    {!loading && list.length === 0 && (
                      <tr><td colSpan={9} className="py-10 text-center text-slate-400">Aucun fournisseur</td></tr>
                    )}
                    {list.map((f, idx) => (
                      <RowFournisseur
                        key={f.id}
                        f={f}
                        idx={idx}
                        selected={f.id === selectedId}
                        onSelect={() => setSelectedId(f.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </main>

        {/* ─────── Panneau latéral performance ─────── */}
        <aside>
          {details ? (
            <FichePerformance d={details} />
          ) : (
            <Card className="rounded-2xl">
              <CardContent className="p-10 text-center text-slate-400 text-sm">Sélectionnez un fournisseur</CardContent>
            </Card>
          )}
        </aside>
      </div>

      <FournisseurFormDialog open={showAdd} onOpenChange={setShowAdd} onSaved={() => { setShowAdd(false); load(); }} />
    </Layout>
  );
}

function KpiCard({ label, value, sub, subColor }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900 mt-1 leading-tight">{value}</p>
      {sub && <p className={`text-xs mt-1 ${subColor || 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}

function RowFournisseur({ f, idx, selected, onSelect }) {
  const cat = CAT_COLORS[f.categorie] || CAT_COLORS.Autre;
  const scoreColor =
    f.score >= 90 ? 'bg-emerald-500 text-white' :
    f.score >= 75 ? 'bg-emerald-600 text-white' :
    f.score >= 60 ? 'bg-amber-500 text-white' :
    'bg-rose-500 text-white';
  const barColor = (v) =>
    v >= 90 ? 'bg-emerald-500' :
    v >= 75 ? 'bg-emerald-500' :
    v >= 60 ? 'bg-amber-400' :
    'bg-rose-500';

  return (
    <tr
      onClick={onSelect}
      className={`border-b border-slate-100 cursor-pointer transition ${selected ? 'bg-blue-50/60 ring-1 ring-blue-200' : 'hover:bg-slate-50/60'}`}
      data-testid={`fournisseur-row-${f.id}`}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-md ${avatarColor(f.code, idx)} text-white font-bold flex items-center justify-center flex-shrink-0`}>
            {initials(f.nom)}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-900 leading-tight">{f.nom}</p>
            <p className="text-[11px] text-slate-500 font-mono">Code {f.code}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-2">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${cat.bg} ${cat.text}`}>{f.categorie}</span>
      </td>
      <td className="py-3 px-2">
        <p className="font-semibold text-slate-800 text-sm leading-tight">{f.contact_nom || '—'}</p>
        <p className="text-[11px] text-slate-500">{f.ville || '—'}</p>
      </td>
      <td className="py-3 px-2 text-right font-bold text-slate-800">{f.nb_commandes}</td>
      <td className="py-3 px-2 text-right font-bold text-slate-800">{fmtShort(f.montant_total)}</td>
      <td className="py-3 px-2">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full ${barColor(f.livraison_temps)}`} style={{ width: `${f.livraison_temps}%` }}></div>
          </div>
          <span className="text-xs font-bold">{f.livraison_temps}%</span>
        </div>
      </td>
      <td className="py-3 px-2">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full ${barColor(f.qualite)}`} style={{ width: `${f.qualite}%` }}></div>
          </div>
          <span className="text-xs font-bold">{f.qualite}%</span>
        </div>
      </td>
      <td className="py-3 px-2 text-center">
        <span className={`inline-flex items-center justify-center w-10 h-7 rounded-full text-xs font-black ${scoreColor}`}>
          {f.score}
        </span>
      </td>
      <td className="py-3 px-4">
        <TendanceBadge tendance={f.tendance} />
      </td>
    </tr>
  );
}

function TendanceBadge({ tendance }) {
  if (tendance > 0) return <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><TrendingUp className="h-3 w-3" /> +{tendance}</span>;
  if (tendance < 0) return <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-600"><TrendingDown className="h-3 w-3" /> {tendance}</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500"><Minus className="h-3 w-3" /> 0</span>;
}

function FichePerformance({ d }) {
  const f = d.fournisseur;
  return (
    <div className="space-y-4 sticky top-4">
      {/* En-tête */}
      <Card className="rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-md ${avatarColor(f.code, 0)} text-white font-bold flex items-center justify-center flex-shrink-0`}>
              {initials(f.nom)}
            </div>
            <div className="min-w-0">
              <h3 className="font-extrabold text-slate-900 leading-tight">{f.nom}</h3>
              <p className="text-xs text-slate-500 font-mono mt-0.5">Code {f.code} · {f.ville || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Carte sombre : partenaire + donut */}
      <Card className="rounded-2xl bg-slate-900 text-white overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <ScoreRing value={f.score} />
            <div className="min-w-0">
              <p className="text-base font-extrabold text-emerald-400 leading-tight">{f.label}</p>
              <p className="text-xs text-slate-300 mt-1">
                Client depuis {d.anciennete}. {f.nb_commandes} commandes, {f.en_cours > 0 ? `${f.en_cours} en cours` : 'aucun retard majeur'}.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance */}
      <Card className="rounded-2xl">
        <CardContent className="p-4">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Performance</h4>
          <div className="space-y-2">
            <PerfRow label="Fiabilité"         value={f.fiabilite} />
            <PerfRow label="Livraison à temps" value={f.livraison_temps} />
            <PerfRow label="Qualité"           value={f.qualite} />
            <PerfRow label="Prix compétitifs"  value={f.prix_competitifs} />
          </div>
        </CardContent>
      </Card>

      {/* Évolution score */}
      <Card className="rounded-2xl">
        <CardContent className="p-4">
          <h4 className="text-sm font-bold text-slate-700 mb-2">Évolution du score (12 mois)</h4>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.evolution_score} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-score" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="mois" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={1} />
                <ReTooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }} formatter={(v) => [`${v}/100`, 'Score']} />
                <Area type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2} fill="url(#g-score)" dot={{ r: 2, fill: '#10b981' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Dernières commandes */}
      <Card className="rounded-2xl">
        <CardContent className="p-4">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Dernières commandes</h4>
          {d.dernieres_commandes.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucune commande</p>
          ) : (
            <div className="space-y-3">
              {d.dernieres_commandes.map((c, i) => (
                <div key={i} className="flex items-start justify-between gap-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-800 leading-tight truncate">{c.titre}{c.qte ? ` · ${c.qte} unités` : ''}</p>
                    <p className="text-slate-500 mt-0.5">{fmtDate(c.date)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-extrabold text-slate-900">{fmtShort(c.montant)}</p>
                    <p className={`text-[10px] ${
                      c.statut === 'livree' ? 'text-emerald-600' :
                      c.statut === 'en_transit' ? 'text-amber-600' : 'text-slate-500'
                    }`}>
                      {c.statut === 'livree' ? 'Livrée' : c.statut === 'en_transit' ? 'En transit' : 'Envoyée'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ScoreRing({ value }) {
  const size = 68, stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (value / 100);
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#10b981" strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round" />
      </svg>
      <span className="absolute text-xl font-black text-white">{value}</span>
    </div>
  );
}

function PerfRow({ label, value }) {
  const color = value >= 90 ? 'bg-emerald-500' : value >= 75 ? 'bg-emerald-500' : value >= 60 ? 'bg-amber-400' : 'bg-rose-500';
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-bold text-slate-900">{value}%</span>
      </div>
      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }}></div>
      </div>
    </div>
  );
}

function FournisseurFormDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    nom: '', code: '', categorie: 'Autre',
    contact_nom: '', telephone: '', email: '', ville: '', pays: 'Chine',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ nom: '', code: '', categorie: 'Autre', contact_nom: '', telephone: '', email: '', ville: '', pays: 'Chine' }); }, [open]);

  const submit = async () => {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/fournisseurs`, form);
      toast.success('Fournisseur créé');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Nouveau fournisseur</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom <span className="text-rose-500">*</span></Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} autoFocus /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Auto si vide" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Services & Prestations','Matériel de bureau','Textile','Imprimerie','Transport & Logistique','Autre'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Contact</Label><Input value={form.contact_nom} onChange={(e) => setForm({ ...form, contact_nom: e.target.value })} /></div>
            <div><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Création…' : 'Créer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
